import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Button,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { capitalize } from "lodash";
import { useConfirm } from "material-ui-confirm";
import { useState } from "react";
import {
  DisplayNameForSubscriptionFrequency,
  DisplayNameForSubscriptionIncludedResultType,
} from "#next/utils/shared";
import { trackAction } from "#next/utils/trackUserActions";
import { RouterOutput, trpc } from "#next/utils/trpc";
import {
  CreateSubscriptionWithSearchSelectorModal,
  EditSubscriptionModal,
} from "#next/components/modals/EditSubscriptionModal";
import { FormModal, FormModalTitle } from "#next/components/modals/FormModal";
import { RenderQuery } from "#next/components/base/RenderQuery";

export const ManageSubscriptionsModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <FormModal open={open} onClose={onClose}>
      <Grid container direction="column">
        <RenderQuery
          useQuery={trpc.searchSubscription.list}
          render={(data) => (
            <>
              <FormModalTitle title="My Subscriptions" />
              <Grid item>
                <ManageSubscriptionsForm subscriptions={data} />
              </Grid>
            </>
          )}
        />
      </Grid>
    </FormModal>
  );
};
type TSubscription = RouterOutput["searchSubscription"]["list"];

const SubscriptionRow = ({
  subscription,
}: {
  subscription: TSubscription[0];
}) => {
  const confirm = useConfirm();
  const deleteSubscription = trpc.searchSubscription.delete.useMutation();
  const trpcUtils = trpc.useContext();

  const [editModalIsOpen, setEditModalIsOpen] = useState(false);

  const handleDelete = async (subscription: TSubscription[0]) => {
    await confirm({
      title: "Confirm Deletion",
      description: `Are you sure you want to permanently delete this subscription?`,
      confirmationText: "Delete Subscription",
    });
    deleteSubscription.mutate(
      {
        id: subscription.id,
      },
      {
        onSuccess: (data) => {
          trpcUtils.searchSubscription.list.setData(undefined, data);
        },
      },
    );
  };

  return (
    <TableRow>
      <TableCell>{subscription.SavedSearch.name}</TableCell>
      <TableCell>
        {capitalize(
          DisplayNameForSubscriptionFrequency[subscription.frequency],
        )}
      </TableCell>
      <TableCell>
        {capitalize(
          DisplayNameForSubscriptionIncludedResultType[
            subscription.includeResults
          ],
        )}
      </TableCell>
      <TableCell>
        <Grid container direction="column">
          {subscription.sendToEmails.sort().map((email, index) => {
            return (
              <Grid item key={email}>
                {index > 0 && <Divider sx={{ my: 1 }} />}
                {email}
              </Grid>
            );
          })}
        </Grid>
      </TableCell>
      <TableCell>
        <IconButton
          onClick={() => {
            trackAction("My Subscriptions > Edit");
            setEditModalIsOpen(true);
          }}
        >
          <Tooltip title="Edit Subscription">
            <EditIcon />
          </Tooltip>
        </IconButton>
        <EditSubscriptionModal
          open={editModalIsOpen}
          subscriptionId={subscription.id}
          onClose={() => setEditModalIsOpen(false)}
        />
      </TableCell>
      <TableCell>
        <IconButton
          onClick={() => {
            trackAction("My Subscriptions > Delete Dialog > Open");
            handleDelete(subscription);
          }}
        >
          <Tooltip title="Delete Subscription">
            <DeleteIcon />
          </Tooltip>
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const ManageSubscriptionsForm = ({
  subscriptions,
}: {
  subscriptions: TSubscription;
}) => {
  const [createModalIsOpen, setCreateModalIsOpen] = useState(false);
  return (
    <Box sx={{ height: 400, width: "100%" }}>
      <Grid container direction="column">
        <Grid item>
          <Grid container justifyContent="flex-start">
            <Grid item sx={{ my: 1 }}>
              <Button
                size="small"
                variant="contained"
                sx={{
                  backgroundColor: "secondary",
                }}
                onClick={() => {
                  trackAction("My Subscriptions > Create");
                  setCreateModalIsOpen(true);
                }}
              >
                Create A Subscription
              </Button>
              <CreateSubscriptionWithSearchSelectorModal
                open={createModalIsOpen}
                onClose={() => setCreateModalIsOpen(false)}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          {subscriptions.length ? (
            // https://smartdevpreneur.com/how-to-customize-material-ui-table-scrolling/
            <TableContainer sx={{ height: "400px" }}>
              <Table sx={{ height: "max-content" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell>Frequency</TableCell>
                    <TableCell>Type of Post</TableCell>
                    <TableCell>Recipients</TableCell>
                    <TableCell>Edit</TableCell>
                    <TableCell>Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptions.map((subscription) => (
                    <SubscriptionRow
                      key={subscription.id}
                      subscription={subscription}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ mt: 1 }}>
              You don&apos;t have any subscriptions.
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
